import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../../config/api';
import { useToast } from '../../hooks/useToast';
import {
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Breadcrumbs } from '../../components/ui/Breadcrumbs';
import { Toast } from '../../components/ui/Toast';

const UserCreate: React.FC = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState<boolean>(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Toast notifications
  const { toasts, removeToast, success, error: showError } = useToast();

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    roles: [] as string[],
    isAdmin: false,
    actif: false
  });

  const availableRoles = ['admin', 'provider', 'partner'];

  const handleCreate = async () => {
    try {
      setSaving(true);

      // Validation
      if (!formData.name || !formData.email || !formData.password || formData.roles.length === 0) {
        showError('Tous les champs sont requis');
        return;
      }

      const token = localStorage.getItem('@TikodAdmin:token');
      const response = await fetch(buildApiUrl('/api/users'), {
        method: 'POST',
        headers: {
          'x-auth-token': token || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de la création de l\'utilisateur');
      }

      success('Utilisateur créé avec succès');
      navigate('/users');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = (event: any) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      roles: typeof value === 'string' ? value.split(',') : value
    }));
  };

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 2, md: 4 }, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs items={[{ label: 'Utilisateurs', path: '/users' }, { label: 'Créer' }]} />

      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        mb={3}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 2, sm: 0 }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/users')}
          sx={{ mr: { sm: 2 }, alignSelf: { xs: 'flex-start', sm: 'auto' } }}
          size={isMobile ? 'small' : 'medium'}
        >
          Retour
        </Button>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontSize: { xs: '1.5rem', md: '2.125rem' },
            textAlign: { xs: 'center', sm: 'left' }
          }}
        >
          Créer un utilisateur
        </Typography>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            label="Nom complet"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            fullWidth
            required
            helperText="Nom et prénom de l'utilisateur"
            size={isMobile ? 'small' : 'medium'}
          />

          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            fullWidth
            required
            helperText="Adresse email qui servira d'identifiant"
            size={isMobile ? 'small' : 'medium'}
          />

          <TextField
            label="Mot de passe"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
            fullWidth
            required
            helperText="Mot de passe sécurisé (minimum 6 caractères)"
            size={isMobile ? 'small' : 'medium'}
          />

          <FormControl fullWidth required>
            <InputLabel size={isMobile ? 'small' : 'medium'}>Rôles</InputLabel>
            <Select
              multiple
              value={formData.roles}
              onChange={handleRoleChange}
              label="Rôles"
              size={isMobile ? 'small' : 'medium'}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {availableRoles.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 3 }
          }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isAdmin}
                  onChange={(e) => setFormData(prev => ({ ...prev, isAdmin: e.target.checked }))}
                  size={isMobile ? 'small' : 'medium'}
                />
              }
              label="Administrateur"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.actif}
                  onChange={(e) => setFormData(prev => ({ ...prev, actif: e.target.checked }))}
                  size={isMobile ? 'small' : 'medium'}
                />
              }
              label="Compte actif"
            />
          </Box>

          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            justifyContent: 'flex-end',
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/users')}
              disabled={saving}
              fullWidth={isMobile}
              size={isMobile ? 'large' : 'medium'}
            >
              Annuler
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleCreate}
              disabled={saving}
              fullWidth={isMobile}
              size={isMobile ? 'large' : 'medium'}
            >
              {saving ? 'Création...' : 'Créer l\'utilisateur'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Toast notifications */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          open={true}
          onClose={() => removeToast(toast.id)}
          message={toast.message}
          severity={toast.severity}
        />
      ))}
    </Container>
  );
};

export default UserCreate; 